import { makeStyles } from "$components/mui-styles";
import {
  FormControl,
  FormControlLabel,
  type FormControlLabelProps,
  FormLabel,
  Radio,
  RadioGroup,
  type RadioGroupProps,
} from "@material-ui/core";
import React from "react";
import HelperText from "./HelperText";

export interface RadioFieldProps {
  disabled?: boolean;
  name: string;
  value?: string | boolean | null;
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  "aria-label"?: string;
  "data-testid"?: string;
  defaultValue?: string;
  resetMarginTop?: boolean;
  options: Omit<FormControlLabelProps, "control">[];
  row?: boolean;
  onChange?: RadioGroupProps["onChange"];
  onBlur?: React.FocusEventHandler;
  fullWidth?: boolean;
  padding?: "dense" | "normal";
}

interface RadioInputProps extends React.HTMLAttributes<HTMLInputElement> {
  "data-testid"?: string;
}

const useFormLabelStyles = makeStyles((theme) => ({
  root: {
    marginLeft: 0,
    marginBottom: theme.spacing(1),
  },
}));

const useInputLabelStyles = makeStyles((theme) => ({
  root: {
    borderRadius: theme.spacing(2.5),
    border: "1px solid",
    borderColor: ({ error }: RadioFieldProps) =>
      error ? theme.palette.error.main : theme.palette.grey[300],
    paddingRight: theme.spacing(2.5),
    marginLeft: 0,
    marginTop: ({ resetMarginTop }: RadioFieldProps) =>
      resetMarginTop ? "0" : theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      marginTop: () => theme.spacing(1),
    },
    transition: theme.transitions.create(["border-color", "box-shadow"], {
      duration: theme.transitions.duration.shortest,
    }),
    "&:focus-within": {
      borderColor: ({ error }: RadioFieldProps) =>
        error ? theme.palette.error.main : theme.palette.grey[300],
      boxShadow: ({ error }: RadioFieldProps) =>
        `0 0 0 1px ${
          error ? theme.palette.error.main : theme.palette.primary.main
        }`,
    },
  },
  label: {
    color: ({ error }: RadioFieldProps) =>
      error ? theme.palette.error.main : theme.palette.text.primary,
  },
}));

const useRadioStyles = makeStyles((theme) => ({
  colorSecondary: {
    "&, &.Mui-checked": {
      color: ({ error }: RadioFieldProps) =>
        error ? theme.palette.error.main : theme.palette.primary.main,
    },
  },
}));

export const RadioGroupField = React.forwardRef(
  (
    props: RadioFieldProps,
    ref: React.Ref<HTMLInputElement>,
  ): React.ReactElement => {
    const {
      disabled = false,
      name,
      value = "",
      label = undefined,
      error = undefined,
      helperText = undefined,
      required = false,
      "aria-label": ariaLabel = label,
      "data-testid": dataTestId = undefined,
      onChange = undefined,
      onBlur = undefined,
      defaultValue = undefined,
      row = true,
      options,
      fullWidth = false,
      padding = "dense",
    } = props;

    const formLabelClasses = useFormLabelStyles();
    const inputLabelClasses = useInputLabelStyles(props);
    const radioClasses = useRadioStyles(props);

    return (
      <FormControl
        required={required}
        error={!!error}
        component="fieldset"
        disabled={disabled}
        fullWidth={fullWidth}
      >
        {label && (
          <FormLabel classes={formLabelClasses} component="legend">
            {label}
          </FormLabel>
        )}
        <RadioGroup
          aria-label={ariaLabel}
          name={name}
          row={row}
          defaultValue={defaultValue}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          data-testid={dataTestId}
        >
          {options.map((opt, index) => (
            <FormControlLabel
              classes={inputLabelClasses}
              key={opt.value as string}
              inputRef={ref}
              checked={!!value && `${opt.value}` === `${value}`}
              style={
                padding === "normal"
                  ? { padding: "12px 16px", borderRadius: 1000 }
                  : undefined
              }
              control={
                <Radio
                  classes={radioClasses}
                  disabled={opt.disabled}
                  style={{
                    padding:
                      padding === "normal" ? "0px 8px 0px 0px" : undefined,
                  }}
                  inputProps={
                    {
                      "data-testid": `${dataTestId ?? name}-${index}`,
                    } as RadioInputProps
                  }
                  required={required}
                />
              }
              {...opt}
            />
          ))}
        </RadioGroup>
        <HelperText error={error}>{helperText}</HelperText>
      </FormControl>
    );
  },
);
